import axios from 'axios';

const isLocalhost = Boolean(
  typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
  )
);

// Dynamic local & live URLs
export const BASE_URL = process.env.REACT_APP_API_URL || (isLocalhost ? "http://localhost:5678/api" : "https://api.rajputsecurityservices.in/api");
export const IMAGE_URL = process.env.REACT_APP_IMAGE_URL || (isLocalhost ? "http://localhost:5678/static" : "https://api.rajputsecurityservices.in/static");

// Simple rate limiting utility
class RateLimiter {
  constructor(maxRequests = 100, timeWindow = 60000) { // 50 requests per minute
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
  }

  canMakeRequest() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.timeWindow);
    return this.requests.length < this.maxRequests;
  }

  addRequest() {
    this.requests.push(Date.now());
  }
}

const rateLimiter = new RateLimiter();

// Create axios instance
export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000, // Reduced to 15 seconds for faster error handling
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and rate limiting
api.interceptors.request.use(
  async (config) => {
    // Check rate limiting and wait if necessary
    if (!rateLimiter.canMakeRequest()) {
      // Wait a short time instead of blocking indefinitely
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    rateLimiter.addRequest();

    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If data is FormData, remove Content-Type to let browser set it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = /\/(login|forgot-password|verify-otp|resend-otp)/.test(requestUrl);
      const onLoginPage = window.location.pathname === '/login';

      // Wrong password / auth-form 401s should not wipe the session or reload the app
      if (!isAuthEndpoint && !onLoginPage) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials, role) => {
    let endpoint;
    if (role === 'admin') {
      endpoint = '/admin/login';
    } else if (role === 'employee') {
      endpoint = '/employee/login';
    } else {
      endpoint = '/manager/login';
    }

    console.log('🔐 API: Login request details:', {
      endpoint: `${BASE_URL}${endpoint}`,
      role,
      credentials: { ...credentials, password: '***' }
    });

    return api.post(endpoint, credentials);
  },
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resendOTP: (data) => api.post('/auth/resend-otp', data),
  updatePassword: (data) => api.post('/auth/verify-otp', data),
};

export const userAPI = {
  // Admin endpoints
  createManager: (data) => api.post('/manager', data),
  createEmployee: (data) => api.post('/employee', data),
  getAllManagers: () => api.get('/manager/all'),
  getAllEmployees: (filters) => api.get('/employee/all', { params: filters }),
  getEmployeesByShift: (shift) => api.get('/employee/all', { params: { shift } }),
  updateManager: (id, data) => api.put(`/manager/${id}`, data),
  updateEmployee: (id, data) => api.put(`/employee/${id}`, data),
  deleteManager: (id) => api.delete(`/manager/${id}`),
  deleteEmployee: (id) => api.delete(`/employee/${id}`),

  // Manager endpoints
  getMyEmployees: () => api.get('/employee/all'), // Manager gets employees assigned to them
  createMyEmployee: (data) => api.post('/employee/manager', data),
  updateMyEmployee: (id, data) => api.put(`/employee/${id}`, data),
  deleteMyEmployee: (id) => api.delete(`/employee/${id}`),

  // Employee endpoints
  getEmployeeDashboard: () => api.get('/employee/dashboard'),
  getEmployeeAttendance: (employeeId, filters) => api.get(`/attendence/${employeeId}`, { params: filters }),

  // Profile
  updateProfile: (data) => api.put('/profile', data),
  changePassword: (data) => api.put('/profile/password', data),
};

export const attendanceAPI = {
  // Admin endpoints - using correct backend routes
  getAllAttendance: (filters) => api.get('/attendence', { params: filters }),
  getAttendanceByEmployee: (employeeId, filters) =>
    api.get(`/attendence/${employeeId}`, { params: filters }),

  // Manager endpoints - using correct endpoints from your specification
  getMyEmployeesAttendance: (filters) =>
    api.get('/attendence', { params: filters }),
  getEmployeeAttendance: (employeeId, filters) =>
    api.get(`/attendence/${employeeId}`, { params: filters }),

  // Employee endpoints
  clockIn: (data) => api.post('/attendence/step-in', data),
  clockOut: (data) => api.post('/attendence/step-out', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getMyAttendance: (filters) => api.get('/attendence', { params: filters }),

  // Update attendance
  updateAttendance: (attendanceId, data) => api.put(`/attendence/${attendanceId}`, data),
  bulkUpdateAttendance: (data) => api.post('/attendence/bulk-update', data),

  // Get geo-fenced attendance (admin dashboard)
  getGeoFencedAttendance: (filters) => api.get('/attendence/geofenced', { params: filters }),

  getAttendanceLogs: (filters) => api.get('/attendence/logs', { params: filters }),
};

export const reportAPI = {
  // Admin reports
  getAttendanceReport: (filters) =>
    api.get('/admin/reports/attendance', { params: filters }),
  getEmployeeReport: (filters) =>
    api.get('/admin/reports/employees', { params: filters }),
  getManagerReport: (filters) =>
    api.get('/admin/reports/managers', { params: filters }),

  // Manager reports
  getMyTeamReport: (filters) =>
    api.get('/manager/reports/team', { params: filters }),
  getMyAttendanceReport: (filters) =>
    api.get('/manager/reports/attendance', { params: filters }),

  // Export reports with WebView support
  exportCSV: (endpoint, filters) =>
    api.get(`${endpoint}/export/csv`, {
      params: filters,
      responseType: 'blob'
    }).then(response => {
      // Check if in WebView and handle download appropriately
      if (window.ReactNativeWebView && window.downloadCSV) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const filename = `export_${new Date().toISOString().split('T')[0]}.csv`;
          window.downloadCSV(base64Data, filename);
        };
        reader.readAsDataURL(response.data);
        return { success: true, message: 'Download initiated in mobile app' };
      } else {
        // Fallback for regular browser
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, message: 'Download completed' };
      }
    }),
  exportPDF: (endpoint, filters) =>
    api.get(`${endpoint}/export/pdf`, {
      params: filters,
      responseType: 'blob'
    }).then(response => {
      // Check if in WebView and handle download appropriately
      if (window.ReactNativeWebView && window.downloadPDF) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const filename = `export_${new Date().toISOString().split('T')[0]}.pdf`;
          window.downloadPDF(base64Data, filename);
        };
        reader.readAsDataURL(response.data);
        return { success: true, message: 'Download initiated in mobile app' };
      } else {
        // Fallback for regular browser
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `export_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, message: 'Download completed' };
      }
    }),
  exportExcel: (endpoint, filters) =>
    api.get(`${endpoint}/export/excel`, {
      params: filters,
      responseType: 'blob'
    }).then(response => {
      // Check if in WebView and handle download appropriately
      if (window.ReactNativeWebView && window.downloadExcel) {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const filename = `export_${new Date().toISOString().split('T')[0]}.xlsx`;
          window.downloadExcel(base64Data, filename);
        };
        reader.readAsDataURL(response.data);
        return { success: true, message: 'Download initiated in mobile app' };
      } else {
        // Fallback for regular browser
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, message: 'Download completed' };
      }
    }),
};

export const uploadAPI = {
  uploadImage: (file, type = 'attendance') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const dashboardAPI = {
  getDashboard: () => api.get('/dashboard'),
};

export const managerAPI = {
  // Admin endpoints for managing managers (using the correct endpoints from your API docs)
  getAllManagers: () => api.get('/manager/all'),
  getManagerById: (id) => api.get(`/manager/${id}`),
  createManager: (data) => api.post('/manager', data),
  updateManager: (id, data) => api.put(`/manager/${id}`, data),
  deleteManager: (id) => api.delete(`/manager/${id}`),
};

export const locationAPI = {
  // Get all locations
  getAllLocations: () => api.get('/location'),

  // Get location by ID
  getLocationById: (id) => api.get(`/location/${id}`),

  // Create new location
  createLocation: (data) => api.post('/location', data),

  // Update location
  updateLocation: (id, data) => api.put(`/location/${id}`, data),

  // Delete location
  deleteLocation: (id) => api.delete(`/location/${id}`),
};

export const siteAPI = {
  // Get all sites with optional filters (status, search)
  getAllSites: (params = {}) => api.get('/sites', { params }),

  // Get site by ID
  getSiteById: (id) => api.get(`/sites/${id}`),

  // Create site
  createSite: (data) => api.post('/sites', data),

  // Update site
  updateSite: (id, data) => api.put(`/sites/${id}`, data),

  // Delete site
  deleteSite: (id) => api.delete(`/sites/${id}`),

  // Get site statistics & summary
  getSiteStats: (id) => api.get(`/sites/${id}/stats`),

  // Get site employees
  getSiteEmployees: (id, params = {}) => api.get(`/sites/${id}/employees`, { params }),

  // Get site supervisors/managers
  getSiteManagers: (id) => api.get(`/sites/${id}/managers`),

  // Get site attendance records
  getSiteAttendance: (id, params = {}) => api.get(`/sites/${id}/attendance`, { params }),

  // Bulk assign employees to site
  assignEmployees: (siteId, employeeIds, managerId = null) =>
    api.post(`/sites/${siteId}/assign-employees`, { employeeIds, managerId }),

  // Bulk unassign employees from site
  unassignEmployees: (siteId, employeeIds) =>
    api.post(`/sites/${siteId}/unassign-employees`, { employeeIds }),

  // Reassign employees from current site to target site
  reassignEmployees: (employeeIds, targetSiteId) =>
    api.post('/sites/reassign-employees', { employeeIds, targetSiteId }),

  // Assign supervisor to site
  assignManager: (siteId, managerId) =>
    api.post(`/sites/${siteId}/assign-manager`, { managerId }),

  // Unassign supervisor from site
  unassignManager: (siteId, managerId) =>
    api.post(`/sites/${siteId}/unassign-manager`, { managerId }),
};

export default api;
